import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'conditionsList', 'actionsList', 'payloadActionsList',
    'actionsContainer', 'payloadFieldContainer', 'skipRoutingContainer'
  ]

  static values = {
    routingMetadataKeys: { type: Array, default: [] }
  }

  static CONDITION_FIELDS = [
    { value: 'current_day_of_week', label: '\u{1F4C5} Dia da semana' },
    { value: 'current_hour', label: '\u{1F550} Hora do dia (0-23)' },
    { value: 'origin_domain', label: '\u{1F310} Domínio de origem' },
    { value: 'title', label: '\u{1F4DD} Título' },
    { value: 'name', label: '\u{1F464} Nome' },
    { value: 'phone', label: '\u{1F4F1} Telefone' },
    { value: 'email', label: '\u{1F4E7} Email' },
    { value: 'crm_id', label: '\u{1F511} CRM ID' },
    { value: 'usage_type', label: '\u{1F3E2} Tipo de uso (residential/commercial)' },
    { value: 'transaction_type', label: '\u{1F4BC} Tipo de transação (rent/sale)' },
    { value: 'intended_transaction_type', label: '\u{1F3AF} Tipo de transação informada (sale/rent/listing_request)' },
    { value: 'offer_type', label: '\u{1F91D} Natureza do contato (cliente ou não)' },
    { value: 'intended_city', label: '\u{1F3D9}\uFE0F Cidade informada' },
    { value: 'intended_neighborhood', label: '\u{1F4CD} Bairro informado' },
    { value: 'property_acquisition?', label: '\u{1F518} Lead de captação (Sim/Não)' },
    { value: 'property_acquisition_desired_transaction', label: '\u{1F3F7}️ Tipo de captação (sale/rent/exchange)' },
    { value: 'visit_date', label: '\u{1F4C5} Data de visita' },
    { value: 'conversation.user_replied?', label: '\u{1F518} Usuário respondeu na conversa (Sim/Não)' },
    { value: 'conversation.engaged_by_pipeline?', label: '\u{1F518} Participou de engajamento automático (Sim/Não)' },
    { value: 'from_massive_engagement?', label: '\u{1F518} Veio de disparo em massa (Sim/Não)' },
    { value: 'listing.development', label: '\u{1F518} É imóvel de lançamento (Sim/Não)' },
    { value: 'listing.list_price', label: '\u{1F4B0} Preço de venda do imóvel' },
    { value: 'listing.rental_price', label: '\u{1F4B0} Preço de aluguel do imóvel' },
    { value: 'listing.location.area', label: '\u{1F4CD} Zona/Área do imóvel' },
    { value: 'agent_phone', label: '\u{1F4DE} Número do agente (WhatsApp)' },
    { value: 'user_messages_count', label: '\u{1F4CA} Número de mensagens do usuário' },
    { value: 'conversion_rank', label: '\u{1F525} Chance de fechar (0 a 99, comparado aos demais leads da imobiliária)' },
    { value: 'user_sent_first_message?', label: '\u{1F518} Usuário enviou a primeira mensagem (Sim/Não)' },
    { value: 'meta_form_data', label: '\u{1F4CB} Dados do formulário Meta' },
    { value: 'campaign_name', label: '\u{1F4E2} Campanha Meta' },
    { value: 'sponsored_ad?', label: '\u{1F518} Veio de anúncio patrocinado (Sim/Não)' },
    { value: 'human_requested?', label: '\u{1F518} Humano solicitado na conversa (Sim/Não)' },
    { value: 'forced_qualification', label: '\u{1F518} Qualificação forçada por tempo (Sim/Não)' },
    { value: 'unknown_answer', label: '\u{1F518} Qualificação por pergunta desconhecida (Sim/Não)' },
    { value: 'manual_qualification', label: '\u{1F518} Qualificação manual (Sim/Não)' },
    { value: 'created_when_unavailable', label: '\u{1F518} Criado em horário de indisponibilidade (Sim/Não)' },
    { value: 'high_priority', label: '\u{1F518} Lead de alta prioridade (Sim/Não)' },
    { value: 'rotation_tag', label: '\u{1F3F7}\uFE0F Tag da roleta' }
  ]

  static ACTION_FIELDS = [
    { value: 'title', label: '\u{1F4DD} Título' },
    { value: 'name', label: '\u{1F464} Nome' },
    { value: 'phone', label: '\u{1F4F1} Telefone' },
    { value: 'email', label: '\u{1F4E7} Email' },
    { value: 'crm_id', label: '\u{1F511} CRM ID' },
    { value: 'tag', label: '\u{1F3F7}\uFE0F Tag' },
    { value: 'intended_transaction_type', label: '\u{1F3AF} Tipo de transação informada' },
    { value: 'intended_city', label: '\u{1F3D9}\uFE0F Cidade informada' },
    { value: 'intended_neighborhood', label: '\u{1F4CD} Bairro informado' },
    { value: 'transaction_type', label: '\u{1F4BC} Tipo de transação' },
    { value: 'real_estate_needs', label: '\u{1F3E2} Necessidades imobiliárias' },
    { value: 'summary', label: '\u{1F4C4} Resumo do lead' },
    { value: 'force_reassignment', label: '\u{1F504} Forçar redistribuição (ignorar corretor atual)' }
  ]

  connect() {
    this.conditionIndex = this._getNextIndex(this.conditionsListTarget, 'conditions')
    if (this.hasActionsListTarget) {
      this.actionIndex = this._getNextIndex(this.actionsListTarget, 'actions')
    }
    if (this.hasPayloadActionsListTarget) {
      this.payloadActionIndex = this._getNextIndex(this.payloadActionsListTarget, 'actions')
    }
  }

  addCondition(event) {
    event.preventDefault()
    const index = this.conditionIndex++
    this.conditionsListTarget.insertAdjacentHTML('beforeend', this._buildConditionItem(index))
  }

  addAction(event) {
    event.preventDefault()
    const index = this.actionIndex++
    this.actionsListTarget.insertAdjacentHTML('beforeend', this._buildActionItem(index))
  }

  addPayloadAction(event) {
    event.preventDefault()
    const index = this.payloadActionIndex++
    this.payloadActionsListTarget.insertAdjacentHTML('beforeend', this._buildPayloadActionItem(index))
  }

  removeItem(event) {
    event.preventDefault()
    const item = event.currentTarget.closest('.condition-item, .action-item')
    if (item) item.remove()
  }

  typeChanged(event) {
    const type = event.target.value
    const isSkipRouting = type === 'Lead::Transformer::SkipRouting'
    const isPayloadField = type === 'Lead::Transformer::PayloadField'
    const isRegular = !isSkipRouting && !isPayloadField && type !== ''

    if (this.hasActionsContainerTarget)
      this.actionsContainerTarget.classList.toggle('hidden', !isRegular)
    if (this.hasPayloadFieldContainerTarget)
      this.payloadFieldContainerTarget.classList.toggle('hidden', !isPayloadField)
    if (this.hasSkipRoutingContainerTarget)
      this.skipRoutingContainerTarget.classList.toggle('hidden', !isSkipRouting)
  }

  // --- Private ---

  _getNextIndex(listElement, nameSegment) {
    if (!listElement) return 0
    const inputs = listElement.querySelectorAll(`[name*="[${nameSegment}]"]`)
    let maxIndex = -1
    inputs.forEach(input => {
      const match = input.name.match(new RegExp(`\\[${nameSegment}\\]\\[(\\d+)\\]`))
      if (match) {
        const idx = parseInt(match[1], 10)
        if (idx > maxIndex) maxIndex = idx
      }
    })
    return maxIndex + 1
  }

  _buildConditionItem(index) {
    const id = `condition-item-new-${Date.now()}-${index}`
    const prefix = `lead_transformer[conditions][${index}]`
    const num = index + 1
    const dynamicFields = (this.routingMetadataKeysValue || []).map(key => ({
      value: `listing.metadata.routing.${key}`,
      label: `\u{1F3F7}\uFE0F Imóvel: ${key} (routing)`
    }))
    const fieldOptions = [...this.constructor.CONDITION_FIELDS, ...dynamicFields]
      .map(f => `<option value="${f.value}">${f.label}</option>`)
      .join('')

    return `
      <div id="${id}" class="bg-gray-50 border border-gray-200 rounded-lg p-4 condition-item"
           data-controller="lead-transformer-condition">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold text-black flex-shrink-0"
                  style="background: linear-gradient(135deg, #FFDE45 0%, #FFD700 100%);">${num}</span>
            <h4 class="text-sm font-medium text-gray-700">Condição ${num}</h4>
          </div>
          <button type="button"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  data-action="click->lead-transformer-form#removeItem">Remover</button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="form-control">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Campo</span></label>
            <select name="${prefix}[field]"
                    class="select select-bordered w-full"
                    data-lead-transformer-condition-target="field"
                    data-action="change->lead-transformer-condition#fieldChanged">
              <option value="">Selecione um campo</option>
              ${fieldOptions}
            </select>
          </div>
          <div class="form-control">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Operador</span></label>
            <select name="${prefix}[operator]"
                    class="select select-bordered w-full"
                    data-lead-transformer-condition-target="operator"
                    data-action="change->lead-transformer-condition#operatorChanged">
              <option value="">Selecione um operador</option>
              <optgroup label="Comparação">
                <option value="equals">= É igual a</option>
                <option value="not_equals">≠ É diferente de</option>
              </optgroup>
              <optgroup label="Texto">
                <option value="contains">Contém</option>
                <option value="not_contains">Não contém</option>
                <option value="starts_with">Começa com</option>
                <option value="ends_with">Termina com</option>
              </optgroup>
              <optgroup label="Presença">
                <option value="present">Tem valor</option>
                <option value="blank">Está vazio</option>
              </optgroup>
              <optgroup label="Número">
                <option value="greater_than">&gt; Maior que</option>
                <option value="greater_than_or_equal">&gt;= Maior ou igual a</option>
                <option value="less_than">&lt; Menor que</option>
                <option value="less_than_or_equal">&lt;= Menor ou igual a</option>
              </optgroup>
            </select>
          </div>
          <div class="form-control" data-lead-transformer-condition-target="valueContainer">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Valor</span></label>
            <input type="text" name="${prefix}[value]"
                   class="input input-bordered w-full"
                   placeholder="valor de comparação"
                   data-lead-transformer-condition-target="value" />
          </div>
        </div>
      </div>
    `
  }

  _buildActionItem(index) {
    const id = `action-item-new-${Date.now()}-${index}`
    const prefix = `lead_transformer[actions][${index}]`
    const num = index + 1
    const fieldOptions = this.constructor.ACTION_FIELDS
      .map(f => `<option value="${f.value}">${f.label}</option>`)
      .join('')

    return `
      <div id="${id}" class="bg-gray-50 border border-gray-200 rounded-lg p-4 action-item">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold text-black flex-shrink-0"
                  style="background: linear-gradient(135deg, #FFDE45 0%, #FFD700 100%);">${num}</span>
            <h4 class="text-sm font-medium text-gray-700">Ação ${num}</h4>
          </div>
          <button type="button"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  data-action="click->lead-transformer-form#removeItem">Remover</button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="form-control">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Campo de Destino</span></label>
            <select name="${prefix}[target_field]" class="select select-bordered w-full">
              <option value="">Selecione um campo</option>
              ${fieldOptions}
            </select>
          </div>
          <div class="form-control">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Valor</span></label>
            <input type="text" name="${prefix}[value]"
                   class="input input-bordered w-full font-mono text-sm"
                   placeholder="Ex: [FACEBOOK] ou {name} - Lead" />
            <p class="mt-1 text-xs text-gray-500">
              Use <code class="bg-gray-100 px-1 rounded text-xs">{campo}</code> para valores dinâmicos. Operações:
              <code class="bg-gray-100 px-1 rounded text-xs">{campo:trim}</code>,
              <code class="bg-gray-100 px-1 rounded text-xs">{campo:remove('texto')}</code>,
              <code class="bg-gray-100 px-1 rounded text-xs">{campo:replace('buscar','substituir')}</code>
            </p>
          </div>
        </div>
      </div>
    `
  }

  _buildPayloadActionItem(index) {
    const id = `action-item-payload-new-${Date.now()}-${index}`
    const prefix = `lead_transformer[actions][${index}]`
    const num = index + 1

    return `
      <div id="${id}" class="bg-gray-50 border border-orange-200 rounded-lg p-4 action-item">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold text-white flex-shrink-0"
                  style="background: linear-gradient(135deg, #F97316 0%, #EA580C 100%);">${num}</span>
            <h4 class="text-sm font-medium text-gray-700">Campo ${num}</h4>
          </div>
          <button type="button"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  data-action="click->lead-transformer-form#removeItem">Remover</button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="form-control">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Campo do Payload</span></label>
            <input type="text" name="${prefix}[target_field]"
                   class="input input-bordered w-full font-mono text-sm"
                   placeholder="Ex: id_roleta" />
          </div>
          <div class="form-control">
            <label class="label py-1"><span class="label-text text-xs font-medium text-gray-700">Valor</span></label>
            <input type="text" name="${prefix}[value]"
                   class="input input-bordered w-full font-mono text-sm"
                   placeholder="Ex: 10" />
            <p class="mt-1 text-xs text-gray-500">
              Use <code class="bg-gray-100 px-1 rounded text-xs">{campo}</code> para valores dinâmicos
            </p>
          </div>
        </div>
      </div>
    `
  }
}
